import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, Plus, Edit, Trash2, Shield, User, Database, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import DatabaseStatus from './DatabaseStatus';
import UserInviteModal from './UserInviteModal';
import { Mail } from 'lucide-react';

// Firebase imports for direct DB access
import { db } from '../utils/firebase/client';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, query, orderBy } from 'firebase/firestore';
import { getCompanyId } from '../utils/tenantContext';

// Firebase Functions for Cloud Function calls
import { getFunctions, httpsCallable } from 'firebase/functions';

interface User {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  pin?: string;
  role: string;
  createdAt: any; // Timestamp or string
  updatedAt?: any;
  homeLocation?: string;
  isSuperAdmin?: boolean;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  // Form States
  const [editUserData, setEditUserData] = useState({
    email: '',
    name: '',
    phone: '',
    pin: '',
    password: '',
    role: 'user',
    homeLocation: '',
  });
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    homeLocation: '',
    pin: ''
  });

  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Current User State
  const [currentUserRole, setCurrentUserRole] = useState<string>('user');
  const [currentUserIsSuperAdmin, setCurrentUserIsSuperAdmin] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  const companyId = getCompanyId();

  useEffect(() => {
    // Get current user from storage/auth
    try {
      const currentUserStr = localStorage.getItem('currentUser');
      if (currentUserStr) {
        const currentUser = JSON.parse(currentUserStr);
        setCurrentUserRole(currentUser.role || 'user');
        setCurrentUserIsSuperAdmin(currentUser.isSuperAdmin || currentUser.role === 'superadmin');
        setCurrentUserEmail(currentUser.email || '');
      }
    } catch (e) {
    }

    if (companyId) {
      loadUsers();
    }
  }, [companyId]);

  const loadUsers = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      // Direct Firestore fetch from subcollection
      const usersRef = collection(db, `companies/${companyId}/users`);
      // Optional: Add sorting if needed, e.g., orderBy('createdAt', 'desc')
      // Note: Ordering requires an index, so keeping it simple first
      const snapshot = await getDocs(usersRef);

      const loadedUsers: User[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        loadedUsers.push({
          id: doc.id,
          email: data.email,
          name: data.name,
          phone: data.phone,
          pin: data.pin,
          role: data.role || 'user',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : (data.createdAt || new Date().toISOString()),
          homeLocation: data.homeLocation,
          isSuperAdmin: data.isSuperAdmin
        });
      });

      // Filter out SuperAdmin users from view unless current user is superadmin
      const filteredUsers = loadedUsers.filter((u: User) =>
        currentUserIsSuperAdmin ? true : (u.role !== 'superadmin' && !u.isSuperAdmin)
      );

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error loading users from database');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      toast.error('Tenant Context missing. Cannot create user.');
      return;
    }

    if (!newUser.email || !newUser.password) {
      toast.error('Email and password are required');
      return;
    }

    setCreating(true);

    try {
      // Call Cloud Function (Enterprise Solution - doesn't log you out!)
      const [firstName, ...lastNameParts] = (newUser.name || newUser.email.split('@')[0]).split(' ');
      const lastName = lastNameParts.join(' ') || firstName;

      const functions = getFunctions();
      const createUserFn = httpsCallable(functions, 'createUser');

      const result = await createUserFn({
        email: newUser.email,
        password: newUser.password,
        firstName: firstName,
        lastName: lastName,
        role: newUser.role === 'user' ? 'pumper' : newUser.role,
        phone: '',
        metadata: {
          pin: newUser.pin,
          homeLocation: newUser.homeLocation
        }
      });

      const data = result.data as { success: boolean; userId: string; email: string; message: string };

      // Reload users to show the new one
      await loadUsers();

      // Reset Form
      setNewUser({
        email: '',
        password: '',
        name: '',
        role: 'user',
        homeLocation: '',
        pin: ''
      });
      setShowCreateForm(false);

      toast.success('User created successfully!', {
        description: 'User can now log in with their email and password'
      });
    } catch (error: any) {
      console.error('Error creating user:', error);

      // Parse Firebase Function errors
      const errorMessage = error.message || 'Unknown error';

      if (errorMessage.includes('already-exists') || errorMessage.includes('email-already-exists')) {
        toast.error('Email already in use');
      } else if (errorMessage.includes('weak-password')) {
        toast.error('Password is too weak (min 8 characters)');
      } else if (errorMessage.includes('invalid-email')) {
        toast.error('Invalid email format');
      } else if (errorMessage.includes('permission-denied')) {
        toast.error('You do not have permission to create users');
      } else {
        toast.error(`Failed to create user: ${errorMessage}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const updateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId || !userToEdit) return;
    if (!editUserData.email) {
      toast.error('Email is required');
      return;
    }

    setUpdating(true);

    try {
      const userRef = doc(db, `companies/${companyId}/users`, userToEdit.id);

      const updateData: any = {
        email: editUserData.email,
        role: editUserData.role,
        name: editUserData.name,
        phone: editUserData.phone,
        pin: editUserData.pin,
        homeLocation: editUserData.homeLocation,
        updatedAt: Timestamp.now()
      };

      // In real app, we might handle password update separately via Admin SDK

      await updateDoc(userRef, updateData);

      // Update Local State
      setUsers(prev => prev.map(u =>
        u.id === userToEdit.id ? { ...u, ...updateData } : u
      ));

      setShowEditForm(false);
      setUserToEdit(null);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUpdating(false);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!companyId) return;
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const userRef = doc(db, `companies/${companyId}/users`, userId);
      await deleteDoc(userRef);

      setUsers(prev => prev.filter(user => user.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const openEditDialog = (user: User) => {
    setUserToEdit(user);
    setEditUserData({
      email: user.email || '',
      name: user.name || '',
      phone: user.phone || '',
      pin: user.pin || '',
      password: '', // Don't pre-fill password
      role: user.role || 'user',
      homeLocation: user.homeLocation || '',
    });
    setShowEditForm(true);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'manager':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'user':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'manager':
        return <Users className="h-4 w-4" />;
      case 'user':
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="users" className="space-y-6 animate-in fade-in duration-500">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="users" className="flex items-center space-x-2">
          <Users className="h-4 w-4" />
          <span>User Management</span>
        </TabsTrigger>
        <TabsTrigger value="database" className="flex items-center space-x-2">
          <Database className="h-4 w-4" />
          <span>Database Status</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="space-y-6">
        {/* User Stats - Redesigned with Gradients */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Users - Blue Gradient */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
            whileHover={{ scale: 1.05, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-500 p-4 shadow-lg"
          >
            <div className="text-center">
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <Users className="h-8 w-8 mx-auto text-white mb-2" strokeWidth={2.5} />
              </motion.div>
              <p className="text-sm text-white/80 mb-1 font-medium">Total Users</p>
              <motion.p
                className="text-3xl font-bold text-white"
                key={users.length}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
              >
                {users.length}
              </motion.p>
            </div>
          </motion.div>

          {/* Administrators - Rose Gradient */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            whileHover={{ scale: 1.05, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 via-rose-600 to-pink-500 p-4 shadow-lg"
          >
            <div className="text-center">
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <Shield className="h-8 w-8 mx-auto text-white mb-2" strokeWidth={2.5} />
              </motion.div>
              <p className="text-sm text-white/80 mb-1 font-medium">Administrators</p>
              <motion.p
                className="text-3xl font-bold text-white"
                key={users.filter(user => user.role === 'admin').length}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
              >
                {users.filter(user => user.role === 'admin').length}
              </motion.p>
            </div>
          </motion.div>

          {/* Field Users - Emerald Gradient */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            whileHover={{ scale: 1.05, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-500 p-4 shadow-lg"
          >
            <div className="text-center">
              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                <User className="h-8 w-8 mx-auto text-white mb-2" strokeWidth={2.5} />
              </motion.div>
              <p className="text-sm text-white/80 mb-1 font-medium">Field Users</p>
              <motion.p
                className="text-3xl font-bold text-white"
                key={users.filter(user => user.role === 'user').length}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
              >
                {users.filter(user => user.role === 'user').length}
              </motion.p>
            </div>
          </motion.div>
        </div>

        <Card className="dark:bg-slate-900 border-gray-200 dark:border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="flex items-center space-x-2 text-xl">
              <Users className="h-5 w-5 text-blue-600" />
              <span>User Directory</span>
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowInviteModal(true)}
                className="dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <Mail className="h-4 w-4 mr-2" />
                Invite User
              </Button>
              <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] dark:bg-slate-900 dark:border-slate-800">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>
                      Add a new user to the oil well management system. Choose their role and access level.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createUser} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          value={newUser.name}
                          onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          className="dark:bg-slate-800 dark:border-slate-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pin">PIN Code (4-digit)</Label>
                        <Input
                          id="pin"
                          value={newUser.pin}
                          onChange={(e) => setNewUser(prev => ({ ...prev, pin: e.target.value }))}
                          placeholder="1234"
                          maxLength={4}
                          className="dark:bg-slate-800 dark:border-slate-700"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="user@example.com"
                        required
                        className="dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Temporary Password <span className="text-red-500">*</span></Label>
                      <Input
                        id="password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter password"
                        required
                        className="dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
                      >
                        <SelectTrigger className="dark:bg-slate-800 dark:border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          <SelectItem value="user">User / Pumper</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>

                          {(currentUserIsSuperAdmin || currentUserEmail === 'admin@oilwellx.com') && (
                            <SelectItem value="admin">Admin</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="homeLocation">Home Location (Optional)</Label>
                      <Input
                        id="homeLocation"
                        type="text"
                        value={newUser.homeLocation}
                        onChange={(e) => setNewUser(prev => ({ ...prev, homeLocation: e.target.value }))}
                        placeholder="123 Main St, City, State 12345"
                        className="dark:bg-slate-800 dark:border-slate-700"
                      />
                      <p className="text-xs text-gray-500">
                        Starting location for route optimization
                      </p>
                    </div>
                    <DialogFooter className="pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCreateForm(false)}
                        className="dark:border-slate-700 dark:hover:bg-slate-800"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {creating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...
                          </>
                        ) : 'Create User'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {/* Edit User Dialog */}
            <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
              <DialogContent className="sm:max-w-[500px] dark:bg-slate-900 dark:border-slate-800">
                <DialogHeader>
                  <DialogTitle>Edit User Accounts</DialogTitle>
                  <DialogDescription>
                    Update user information and permissions.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={updateUser} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Full Name</Label>
                      <Input
                        id="edit-name"
                        value={editUserData.name}
                        onChange={(e) => setEditUserData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="John Doe"
                        className="dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-pin">PIN Code</Label>
                      <Input
                        id="edit-pin"
                        value={editUserData.pin}
                        onChange={(e) => setEditUserData(prev => ({ ...prev, pin: e.target.value }))}
                        placeholder="1234"
                        maxLength={4}
                        className="dark:bg-slate-800 dark:border-slate-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email Address <span className="text-red-500">*</span></Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editUserData.email}
                      onChange={(e) => setEditUserData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="user@example.com"
                      required
                      className="dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-phone">Phone Number</Label>
                    <Input
                      id="edit-phone"
                      type="tel"
                      value={editUserData.phone}
                      onChange={(e) => setEditUserData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-role">Role</Label>
                    <Select
                      value={editUserData.role}
                      onValueChange={(value) => setEditUserData(prev => ({ ...prev, role: value }))}
                    >
                      <SelectTrigger className="dark:bg-slate-800 dark:border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        <SelectItem value="user">User / Pumper</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        {(currentUserIsSuperAdmin || currentUserEmail === 'admin@oilwellx.com') && (
                          <SelectItem value="admin">Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-homeLocation">Home Location</Label>
                    <Input
                      id="edit-homeLocation"
                      type="text"
                      value={editUserData.homeLocation}
                      onChange={(e) => setEditUserData(prev => ({ ...prev, homeLocation: e.target.value }))}
                      placeholder="123 Main St, City, State 12345"
                      className="dark:bg-slate-800 dark:border-slate-700"
                    />
                  </div>

                  <DialogFooter className="pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false);
                        setUserToEdit(null);
                      }}
                      className="dark:border-slate-700 dark:hover:bg-slate-800"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updating} className="bg-blue-600 hover:bg-blue-700 text-white">
                      {updating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                        </>
                      ) : 'Save Changes'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <div className="space-y-3">
              {users.map((user) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="border rounded-lg p-4 bg-white dark:bg-slate-900 dark:border-slate-700 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-500">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {user.name || user.email.split('@')[0]}
                        </h4>
                        <div className="flex items-center text-sm text-gray-500 gap-2">
                          <span>{user.email}</span>
                          <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                          <span>Added {formatDate(user.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Badge className={`${getRoleBadgeColor(user.role)} border-0`}>
                        {user.role}
                      </Badge>

                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => openEditDialog(user)}
                        >
                          <Edit className="h-4 w-4 text-gray-500" />
                        </Button>

                        {(currentUserIsSuperAdmin || (currentUserRole === 'admin' && user.role !== 'admin')) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => deleteUser(user.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {users.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500">
                  <div className="bg-gray-100 dark:bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No users found</h3>
                  <p className="text-sm">Create your first user to get started</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Descriptions */}
        <Card className="dark:bg-slate-900 dark:border-slate-800 mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Access Levels</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20">
                <Shield className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-900 dark:text-red-200">Administrator</h4>
                  <p className="text-sm text-red-700 dark:text-red-300/80 mt-1">
                    Full access to all features including user management, system settings, and all well data. Can create and delete users.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20">
                <Users className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-200">Manager</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300/80 mt-1">
                    Access to all wells and production data, analytics, and route optimization. Cannot manage users.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20">
                <User className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-200">User / Pumper</h4>
                  <p className="text-sm text-green-700 dark:text-green-300/80 mt-1">
                    Can enter well data, view assigned wells, and access basic analytics. Limited administrative access.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="database">
        <DatabaseStatus />
      </TabsContent>

      <UserInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          // Refresh user list if necessary, although invites are pending until accepted
          toast.success('Invitation sent successfully');
        }}
        user={{
          id: 'current-user-id', // We should ideally pass real user ID here if available in context
          name: currentUserEmail,
          email: currentUserEmail
        }}
        company={{
          id: companyId || 'default',
          name: 'Your Company' // We should ideally pass real company name
        }}
      />
    </Tabs>
  );
}